<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ParticipantManagement from './ParticipantManagement.vue';

interface Props {
  eventId: number;
  isVisible: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
  'participants-updated': [];
}>();

const { t } = useI18n();
const isActionLoading = ref(false);

const closeModal = () => {
  // Completely prevent closing if any action is in progress
  if (isActionLoading.value) {
    return;
  }
  emit('close');
};
</script>

<template>
  <div v-if="isVisible" class="modal-overlay" @click="isActionLoading ? undefined : closeModal()">
    <div class="modal-content" @click.stop :class="{ 'loading': isActionLoading }">
      <div class="modal-header">
        <h2>{{ t('participant_management.title') }}</h2>
        <div v-if="isActionLoading" class="loading-indicator">
          <span class="loading-spinner">⏳</span>
          <span>{{ t('participant_management.processing') }}</span>
        </div>
        <button class="close-button" @click="closeModal" :disabled="isActionLoading">
          <i class="bi bi-x"></i>
        </button>
      </div>

      <div class="modal-body">
        <ParticipantManagement
          :event-id="eventId"
          @action-loading="isActionLoading = $event"
          @participants-updated="$emit('participants-updated')"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background-color: #efd8b0;
  border-radius: 8px;
  border: 1px solid #bd9c65;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #bd9c65;
  background-color: #c29c58;
  color: #60451f;
}

.modal-header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: bold;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: #60451f;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(96, 69, 31, 0.1);
  }
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

.modal-content.loading {
  pointer-events: none;

  .participant-actions {
    pointer-events: auto;
  }
}

.close-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #60451f;
  font-size: 14px;
  font-weight: bold;
}

.loading-spinner {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
    align-items: flex-start;
    padding-top: 20px;
  }

  .modal-content {
    max-width: 100%;
    max-height: calc(100vh - 40px);
    border-radius: 6px;
  }

  .modal-header {
    padding: 15px;
  }

  .modal-header h2 {
    font-size: 18px;
  }

  .close-button {
    width: 28px;
    height: 28px;
    font-size: 20px;
  }

  .loading-indicator {
    font-size: 13px;
    gap: 6px;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 5px;
    padding-top: 15px;
  }

  .modal-content {
    max-height: calc(100vh - 30px);
    border-radius: 4px;
  }

  .modal-header {
    padding: 12px;
  }

  .modal-header h2 {
    font-size: 16px;
  }

  .close-button {
    width: 26px;
    height: 26px;
    font-size: 18px;
  }

  .loading-indicator {
    font-size: 12px;
    gap: 5px;
  }
}
</style>
